import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import { WindowState } from '../overlays/windowState';

const PROMPT_TRUNCATE_LENGTH = 300;
const TRAINING_POLL_INTERVAL_MS = 3000;
const TRAINING_POLL_TIMEOUT_MS = 6 * 60 * 1000;

const SPECIALTY_FIELDS = `
  id name description status generatedPrompt promptVersion trainingDataVersion
  trainedTrainingDataVersion trainingError canRevert createdAt updatedAt
`;

const GET_CUSTOM_SPECIALTY = `
  query GetCustomSpecialty($teamId: ID!, $specialtyId: ID!) {
    getCustomSpecialty(teamId: $teamId, specialtyId: $specialtyId) {
      ${SPECIALTY_FIELDS}
    }
    getCustomSpecialtyTrainingData(teamId: $teamId, specialtyId: $specialtyId) {
      id fileName role status initial downloadUrl createdAt
    }
  }
`;

const GET_TRAINING_STATUS = `
  query TrainingStatus($teamId: ID!, $specialtyId: ID!) {
    getCustomSpecialty(teamId: $teamId, specialtyId: $specialtyId) { ${SPECIALTY_FIELDS} }
  }
`;

const GENERATE_PROMPT = `
  mutation GenerateCustomSpecialtyPrompt($teamId: ID!, $specialtyId: ID!) {
    generateCustomSpecialtyPrompt(teamId: $teamId, specialtyId: $specialtyId) {
      customSpecialty { ${SPECIALTY_FIELDS} }
    }
  }
`;

const REVERT_PROMPT = `
  mutation RevertCustomSpecialtyPrompt($teamId: ID!, $specialtyId: ID!) {
    revertCustomSpecialtyPrompt(teamId: $teamId, specialtyId: $specialtyId) { ${SPECIALTY_FIELDS} }
  }
`;

const GET_TRAINING_DATA = `
  query TrainingDataOnly($teamId: ID!, $specialtyId: ID!) {
    getCustomSpecialtyTrainingData(teamId: $teamId, specialtyId: $specialtyId) {
      id fileName role status initial downloadUrl createdAt
    }
  }
`;

const REQUEST_TRAINING_DATA_UPLOAD = `
  mutation RequestCustomSpecialtyTrainingDataUpload($args: RequestCustomSpecialtyTrainingDataUploadInput!) {
    requestCustomSpecialtyTrainingDataUpload(args: $args) {
      trainingData { id }
      uploadUrl
      uploadFields { key value }
    }
  }
`;

const CONFIRM_TRAINING_DATA_UPLOAD = `
  mutation ConfirmCustomSpecialtyTrainingDataUpload($teamId: ID!, $specialtyId: ID!, $trainingDataId: ID!) {
    confirmCustomSpecialtyTrainingDataUpload(
      teamId: $teamId specialtyId: $specialtyId trainingDataId: $trainingDataId
    ) { id status }
  }
`;

const REMOVE_TRAINING_DATA = `
  mutation RemoveCustomSpecialtyTrainingData($teamId: ID!, $specialtyId: ID!, $trainingDataId: ID!) {
    removeCustomSpecialtyTrainingData(
      teamId: $teamId specialtyId: $specialtyId trainingDataId: $trainingDataId
    ) { id status }
  }
`;

const TRAINING_DATA_EXTENSIONS = new Set(['pdf', 'docx', 'txt']);
const TRAINING_DATA_MAX_BYTES = 25 * 1024 * 1024;

type CustomSpecialtyDetail = {
  id: string;
  name: string;
  description?: string;
  status: string;
  generatedPrompt?: string;
  createdAt?: string;
  updatedAt?: string;
  promptVersion?: number;
  trainingDataVersion?: number;
  trainedTrainingDataVersion?: number;
  trainingError?: string;
  canRevert?: boolean;
};

export type CustomSpecialtyTrainingData = {
  id: string;
  fileName: string;
  role: 'Initial' | 'Exemplar' | 'Reference';
  status: 'Ready';
  initial?: boolean;
  downloadUrl?: string;
  createdAt: string;
};

type GetCustomSpecialtyResponse = {
  getCustomSpecialty?: CustomSpecialtyDetail | null;
  getCustomSpecialtyTrainingData?: CustomSpecialtyTrainingData[];
};

export class CustomSpecialtyDrawerState extends StoreNode {
  constructor(store: Store) {
    super(store);
    makeObservable(this);
  }

  readonly window = new WindowState(this.store);

  @observable specialty: CustomSpecialtyDetail | null = null;
  @observable isLoading = false;
  @observable error: string | null = null;
  @observable isPromptExpanded = false;
  @observable trainingData: CustomSpecialtyTrainingData[] = [];
  @observable selectedTrainingDataFile: File | null = null;
  @observable trainingDataRole: 'Exemplar' | 'Reference' = 'Exemplar';
  @observable isUploadingTrainingData = false;
  @observable removingTrainingDataId: string | null = null;
  @observable trainingDataError: string | null = null;
  @observable isRetraining = false;
  @observable isReverting = false;

  private teamId: string | null = null;
  private specialtyId: string | null = null;

  @computed
  get truncatedPrompt(): string {
    const prompt = this.specialty?.generatedPrompt;
    if (!prompt) return '';
    if (prompt.length <= PROMPT_TRUNCATE_LENGTH) return prompt;
    return prompt.slice(0, PROMPT_TRUNCATE_LENGTH) + '...';
  }

  @computed
  get hasTrainingChanges(): boolean {
    return (this.specialty?.trainingDataVersion ?? 0)
      !== (this.specialty?.trainedTrainingDataVersion ?? 0);
  }

  @action
  async open(teamId: string, specialtyId: string) {
    this.isLoading = true;
    this.error = null;
    this.specialty = null;
    this.isPromptExpanded = false;
    this.teamId = teamId;
    this.specialtyId = specialtyId;
    this.trainingData = [];
    this.selectedTrainingDataFile = null;
    this.trainingDataError = null;
    this.isRetraining = false;
    this.isReverting = false;
    this.window.open();

    try {
      const [res] = await this.store.apiService.runRequest<GetCustomSpecialtyResponse>(
        GET_CUSTOM_SPECIALTY,
        { teamId, specialtyId }
      );
      runInAction(() => {
        if (res?.getCustomSpecialty) {
          this.specialty = res.getCustomSpecialty;
          this.trainingData = res.getCustomSpecialtyTrainingData ?? [];
        } else {
          this.error = 'Custom specialty not found.';
        }
        this.isLoading = false;
      });
    } catch {
      runInAction(() => {
        this.error = 'Failed to load custom specialty details.';
        this.isLoading = false;
      });
    }
  }

  @action
  close() {
    this.window.close();
    this.specialty = null;
    this.error = null;
    this.teamId = null;
    this.specialtyId = null;
    this.trainingData = [];
    this.selectedTrainingDataFile = null;
    this.trainingDataError = null;
    this.isRetraining = false;
    this.isReverting = false;
  }

  @action
  togglePromptExpanded() {
    this.isPromptExpanded = !this.isPromptExpanded;
  }

  @action
  setTrainingDataFile(file: File | null) {
    this.selectedTrainingDataFile = file;
    this.trainingDataError = null;
  }

  @action
  setTrainingDataRole(role: 'Exemplar' | 'Reference') {
    this.trainingDataRole = role;
  }

  @action
  async uploadTrainingData() {
    const file = this.selectedTrainingDataFile;
    const teamId = this.teamId;
    const specialtyId = this.specialtyId;
    if (!file || !teamId || !specialtyId) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !TRAINING_DATA_EXTENSIONS.has(extension)) {
      this.trainingDataError = 'Training data must be a PDF, DOCX, or TXT document.';
      return;
    }
    if (file.size > TRAINING_DATA_MAX_BYTES) {
      this.trainingDataError = 'Training data must be 25 MB or smaller.';
      return;
    }

    this.isUploadingTrainingData = true;
    this.trainingDataError = null;
    try {
      const [requested, requestError] = await this.store.apiService.runRequest<{
        requestCustomSpecialtyTrainingDataUpload?: {
          trainingData: { id: string };
          uploadUrl: string;
          uploadFields: Array<{ key: string; value: string }>;
        };
      }>(REQUEST_TRAINING_DATA_UPLOAD, { args: {
        teamId,
        specialtyId,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        role: this.trainingDataRole,
      } });
      const upload = requested?.requestCustomSpecialtyTrainingDataUpload;
      if (requestError || !upload) throw requestError ?? new Error('Upload could not be prepared.');

      const [, uploadError] = await this.store.apiService.uploadRequest({
        file,
        url: upload.uploadUrl,
        data: upload.uploadFields,
      }).start();
      if (uploadError) throw uploadError;

      const [, confirmError] = await this.store.apiService.runRequest(
        CONFIRM_TRAINING_DATA_UPLOAD,
        { teamId, specialtyId, trainingDataId: upload.trainingData.id },
      );
      if (confirmError) throw confirmError;

      await this.refreshTrainingData();
      await this.refreshTrainingStatus();
      runInAction(() => { this.selectedTrainingDataFile = null; });
    } catch (error: unknown) {
      runInAction(() => {
        this.trainingDataError = error instanceof Error ? error.message : 'Training data upload failed.';
      });
    } finally {
      runInAction(() => { this.isUploadingTrainingData = false; });
    }
  }

  @action
  async removeTrainingData(trainingDataId: string) {
    if (!this.teamId || !this.specialtyId || trainingDataId === 'initial') return;
    this.removingTrainingDataId = trainingDataId;
    this.trainingDataError = null;
    try {
      const [, error] = await this.store.apiService.runRequest(REMOVE_TRAINING_DATA, {
        teamId: this.teamId,
        specialtyId: this.specialtyId,
        trainingDataId,
      });
      if (error) throw error;
      runInAction(() => {
        this.trainingData = this.trainingData.filter(item => item.id !== trainingDataId);
      });
      await this.refreshTrainingStatus();
    } catch (error: unknown) {
      runInAction(() => {
        this.trainingDataError = error instanceof Error ? error.message : 'Training data could not be removed.';
      });
    } finally {
      runInAction(() => { this.removingTrainingDataId = null; });
    }
  }

  @action
  async retrain() {
    const teamId = this.teamId;
    const specialtyId = this.specialtyId;
    if (!teamId || !specialtyId || !this.hasTrainingChanges || this.isRetraining) return;
    this.isRetraining = true;
    this.trainingDataError = null;
    try {
      const [result, error] = await this.store.apiService.runRequest<{
        generateCustomSpecialtyPrompt?: { customSpecialty: CustomSpecialtyDetail };
      }>(GENERATE_PROMPT, { teamId, specialtyId });
      if (error || !result?.generateCustomSpecialtyPrompt) throw error ?? new Error('Retraining could not start.');
      await this.awaitTrainingResult(
        teamId,
        specialtyId,
        result.generateCustomSpecialtyPrompt.customSpecialty.promptVersion ?? 0,
      );
    } catch (error: unknown) {
      runInAction(() => {
        this.trainingDataError = error instanceof Error ? error.message : 'Retraining failed.';
      });
    } finally {
      runInAction(() => { this.isRetraining = false; });
    }
  }

  @action
  async revert() {
    const teamId = this.teamId;
    const specialtyId = this.specialtyId;
    if (!teamId || !specialtyId || !this.specialty?.canRevert || this.isReverting) return;
    this.isReverting = true;
    this.trainingDataError = null;
    try {
      const [result, error] = await this.store.apiService.runRequest<{
        revertCustomSpecialtyPrompt?: CustomSpecialtyDetail;
      }>(REVERT_PROMPT, { teamId, specialtyId });
      if (error || !result?.revertCustomSpecialtyPrompt) throw error ?? new Error('Revert failed.');
      runInAction(() => { this.specialty = result.revertCustomSpecialtyPrompt!; });
    } catch (error: unknown) {
      runInAction(() => {
        this.trainingDataError = error instanceof Error ? error.message : 'Revert failed.';
      });
    } finally {
      runInAction(() => { this.isReverting = false; });
    }
  }

  private async awaitTrainingResult(teamId: string, specialtyId: string, previousPromptVersion: number) {
    const deadline = Date.now() + TRAINING_POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await new Promise(resolve => { setTimeout(resolve, TRAINING_POLL_INTERVAL_MS); });
      if (!this.isRetraining || this.teamId !== teamId || this.specialtyId !== specialtyId) return;
      const [result, error] = await this.store.apiService.runRequest<GetCustomSpecialtyResponse>(
        GET_TRAINING_STATUS,
        { teamId, specialtyId },
      );
      if (error || !result?.getCustomSpecialty) continue;
      const specialty = result.getCustomSpecialty;
      if (specialty.trainingError) throw new Error(specialty.trainingError);
      if ((specialty.promptVersion ?? 0) > previousPromptVersion) {
        runInAction(() => { this.specialty = specialty; });
        return;
      }
    }
    throw new Error('Retraining is taking longer than expected.');
  }

  private async refreshTrainingStatus() {
    if (!this.teamId || !this.specialtyId) return;
    const [result, error] = await this.store.apiService.runRequest<GetCustomSpecialtyResponse>(
      GET_TRAINING_STATUS,
      { teamId: this.teamId, specialtyId: this.specialtyId },
    );
    if (error) throw error;
    if (result?.getCustomSpecialty) runInAction(() => { this.specialty = result.getCustomSpecialty!; });
  }

  private async refreshTrainingData() {
    if (!this.teamId || !this.specialtyId) return;
    const [result, error] = await this.store.apiService.runRequest<{
      getCustomSpecialtyTrainingData?: CustomSpecialtyTrainingData[];
    }>(GET_TRAINING_DATA, { teamId: this.teamId, specialtyId: this.specialtyId });
    if (error) throw error;
    runInAction(() => {
      this.trainingData = result?.getCustomSpecialtyTrainingData ?? [];
    });
  }
}
